import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  HStack,
  Text,
  VStack,
} from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import { isEmpty, omitBy } from 'lodash';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, string } from 'yup';
import { useSendCalculation } from '../../api/sendCalculation';
import EmailCalculationModalFields from './EmailCalculationModalFields';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quoteId: string;
  isExtendedInput?: boolean;
}

const schema = object({
  email: string().email('INVALID_EMAIL').required('REQUIRED'),
  initials: string(),
  last_name: string(),
  gender: string(),
});

export type EmailCalculationFormValues = InferType<typeof schema>;

const EmailCalculationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  quoteId,
  isExtendedInput,
}) => {
  const { t } = useTranslation();
  const methods = useForm<EmailCalculationFormValues>({
    resolver: yupResolver(schema),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = methods;
  const { mutateAsync: sendCalculation } = useSendCalculation();

  const handleSend = handleSubmit(async (data) => {
    const { email, ...other } = data;
    try {
      await sendCalculation({
        quoteId,
        payload: {
          action_url: window.location.href,
          emails: [data.email],
          ...omitBy(other, isEmpty),
        },
      });
      cogoToast.success(
        t(
          'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_SEND-CALCULATION_SUCCESS',
        ),
      );
    } catch (e) {
      console.error(e);
      cogoToast.error(
        t(
          'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_SEND-CALCULATION_FAILED',
        ),
      );
    }
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <FormProvider {...methods}>
        <ModalContent>
          <ModalHeader>
            {t(
              'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_SEND-CALCULATION',
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody as={VStack} spacing="4">
            <Text color="gray.700">
              {t(
                'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_ACTIONS_SEND-CALCULATION_MODAL_DESCRIPTION',
              )}
            </Text>
            <EmailCalculationModalFields isExtended={isExtendedInput} />
          </ModalBody>
          <ModalFooter as={HStack}>
            <Button variant="ghost" onClick={onClose}>
              {t('CLOSE')}
            </Button>
            <Button onClick={handleSend} isLoading={isSubmitting}>
              {t('SEND')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </FormProvider>
    </Modal>
  );
};

export default EmailCalculationModal;
